import { useCallback, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardIcon,
  PaperAirplaneIcon,
  StopIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { inferenceService, CatalogEntry, PriceRow } from '../services/inferenceApi';
import { env } from '../config/env';
import { buildSnippets } from './snippets';
import { resolveSelectedModel } from './playgroundModel';
import { CATALOG_CATEGORIES, formatPrice } from '../utils/catalog';
import GenerationPanel from '../components/playground/GenerationPanel';
import CompareChat from '../components/playground/CompareChat';
import MessageList from '../components/playground/MessageList';
import { useChatSession } from '../hooks/useChatSession';
import { Page, PageHeader, InferenceTabs, INFERENCE_EYEBROW } from '../components/console/ui';

// Categories routed to the media generation panel instead of the chat pane.
const MEDIA_CATEGORIES = ['image', 'video', 'speech', 'transcription', 'audio'];

const GATEWAY_BASE_URL = `${env.INFERENCE_API_URL.replace(/\/$/, '')}/v1`;

type AuthMode = 'session' | 'key';
type InspectorTab = 'request' | 'response' | 'code';
type CodeLang = 'curl' | 'python' | 'javascript';
type Mode = 'chat' | 'compare';

const Playground = () => {
  // Seeded once from ?model= (Models page "Try in Playground" deep link).
  // Compare mode lives in the URL (?mode=compare&a=&b=) so it can be shared.
  const [searchParams, setSearchParams] = useSearchParams();
  const mode: Mode = searchParams.get('mode') === 'compare' ? 'compare' : 'chat';
  const compareA = searchParams.get('a') ?? '';
  const compareB = searchParams.get('b') ?? '';
  // Shared cache with the Models/Landing pages (queryKey: ['catalog']).
  const { data: catalogData } = useQuery({
    queryKey: ['catalog'],
    queryFn: inferenceService.getCatalog,
  });
  const models: CatalogEntry[] = useMemo(() => catalogData?.models ?? [], [catalogData]);
  const catalogCurrency = catalogData?.currency ?? 'INR';

  const { data: pricing } = useQuery({
    queryKey: ['public-pricing'],
    queryFn: () => inferenceService.getPublicPricing().then((r) => r.data.models),
  });
  const priceByName = useMemo(
    () => new Map<string, PriceRow>((pricing ?? []).map((p: PriceRow) => [p.display_name, p])),
    [pricing]
  );
  const chatModels = useMemo(() => models.filter((m) => !MEDIA_CATEGORIES.includes(m.category)), [models]);

  const [model, setModel] = useState(() => searchParams.get('model') ?? '');
  const [temperature, setTemperature] = useState(0.7);
  // 4096 default: reasoning models spend thinking tokens from the same
  // budget, and 1024 often ran out before the answer started.
  const [maxTokens, setMaxTokens] = useState(4096);
  const [input, setInput] = useState('');

  // Auth selector: session JWT (default) or a pasted psk_ key.
  const [authMode, setAuthMode] = useState<AuthMode>('session');
  const [authKey, setAuthKey] = useState<string | null>(null);

  // Inspector panel state.
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [inspectorTab, setInspectorTab] = useState<InspectorTab>('request');
  const [codeLang, setCodeLang] = useState<CodeLang>('curl');
  const [lastRequest, setLastRequest] = useState<Record<string, unknown> | null>(null);
  const [lastRawResponse, setLastRawResponse] = useState('');

  const selectedModel = resolveSelectedModel(model, models);

  // Category-routed panel: media categories render GenerationPanel instead
  // of the chat pane below. text/embeddings/rerank/unknown fall through to chat.
  const entry = models.find((m) => m.display_name === selectedModel);
  const isMedia = Boolean(entry && MEDIA_CATEGORIES.includes(entry.category));

  const modelsByCategory = useMemo(() => {
    const groups = new Map<string, CatalogEntry[]>();
    for (const m of models) {
      const key = CATALOG_CATEGORIES.some((c) => c.key === m.category) ? m.category : 'other';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(m);
    }
    return groups;
  }, [models]);

  const snippets = useMemo(
    () =>
      buildSnippets({
        baseUrl: GATEWAY_BASE_URL,
        model: selectedModel || 'model-name',
        apiKeyLabel: 'PODSTACK_API_KEY',
      }),
    [selectedModel]
  );

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied');
  };

  // Pasted API keys often carry copy/paste whitespace - always trim.
  const trimmedAuthKey = (authKey ?? '').trim();
  const keyMissing = authMode === 'key' && !trimmedAuthKey;
  // Hoisted out of `run` (unchanged expression) so the GenerationPanel prop
  // below can reuse the same derivation.
  const apiKeyOverride = authMode === 'key' && trimmedAuthKey ? trimmedAuthKey : null;

  const session = useChatSession({
    model: selectedModel,
    temperature,
    maxTokens,
    apiKeyOverride,
    priceByName,
    onRequest: (body) => {
      setLastRequest(body);
      setLastRawResponse('');
    },
    onRawDelta: (delta) => setLastRawResponse((cur) => cur + delta),
  });
  const { messages, busy } = session;

  const send = () => {
    if (keyMissing) return;
    if (session.send(input)) setInput('');
  };

  // A truncated ("length") last answer can be retried with a bigger budget:
  // drop it and re-stream the same conversation.
  const retryTokens = Math.max(4096, maxTokens * 2);
  const retryTruncated = () => {
    if (busy) return;
    setMaxTokens(retryTokens);
    session.retryTruncated(retryTokens);
  };

  const setMode = (next: Mode) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'compare') {
      params.set('mode', 'compare');
      // Start the comparison from the model already picked, when it can chat.
      if (!params.get('a') && selectedModel && !isMedia) params.set('a', selectedModel);
    } else {
      params.delete('mode');
    }
    setSearchParams(params, { replace: true });
  };

  const setCompareModels = useCallback(
    (a: string, b: string) => {
      setSearchParams(
        (cur) => {
          const params = new URLSearchParams(cur);
          params.set('a', a);
          params.set('b', b);
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const entryPrice = entry ? formatPrice(entry, catalogCurrency) : null;
  const fieldLabel = 'mb-1.5 block text-xs text-ps-muted';
  const segBtn = (on: boolean) =>
    `flex-1 rounded-[5px] py-1.5 capitalize transition-colors ${
      on ? 'bg-ps-accent-soft text-ps-accent' : 'text-ps-muted hover:text-ps-text'
    }`;
  const preCls =
    'max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-md border border-ps-border bg-ps-chrome p-3 text-[11px] leading-relaxed text-ps-muted';
  const copyBtnCls =
    'absolute right-2 top-2 rounded-md border border-ps-strong bg-ps-raised p-1 text-ps-muted hover:text-ps-text';

  return (
    <Page>
      <PageHeader
        eyebrow={INFERENCE_EYEBROW}
        title="Playground"
        subtitle={
          mode === 'compare'
            ? 'Send a prompt to two models at once and compare replies, speed and cost side by side. Each request is billed like an API call.'
            : 'Chat with any model in the browser. Requests are billed like API calls, and each reply shows its tokens and cost.'
        }
        actions={
          mode === 'chat' && !isMedia ? (
            <>
              <button
                type="button"
                className="ps-btn2"
                onClick={() => {
                  setInspectorOpen(true);
                  setInspectorTab('code');
                }}
              >
                View code
              </button>
              <button
                type="button"
                className="ps-btn2"
                onClick={session.clear}
                disabled={busy || messages.length === 0}
              >
                <TrashIcon className="h-4 w-4" /> Clear chat
              </button>
            </>
          ) : undefined
        }
      />

      <InferenceTabs />

      <div className="ps-seg self-start" role="group" aria-label="Playground mode">
        <button type="button" aria-pressed={mode === 'chat'} className={mode === 'chat' ? 'ps-seg-on' : ''} onClick={() => setMode('chat')}>
          Chat
        </button>
        <button
          type="button"
          aria-pressed={mode === 'compare'}
          className={mode === 'compare' ? 'ps-seg-on' : ''}
          onClick={() => setMode('compare')}
        >
          Compare models
        </button>
      </div>

      <div className="flex flex-wrap items-stretch gap-5">
        {mode === 'compare' ? (
          <CompareChat
            models={chatModels}
            currency={catalogCurrency}
            modelA={compareA}
            modelB={compareB}
            onModelsChange={setCompareModels}
            temperature={temperature}
            maxTokens={maxTokens}
            apiKeyOverride={apiKeyOverride}
            keyMissing={keyMissing}
            priceByName={priceByName}
          />
        ) : isMedia && entry ? (
          <div className="min-w-0 flex-[3_1_520px]">
            <GenerationPanel key={entry.id} entry={entry} apiKeyOverride={apiKeyOverride} currency={catalogCurrency} />
          </div>
        ) : (
          <section aria-label="Conversation" className="ps-card flex min-w-0 flex-[3_1_520px] flex-col">
            <div className="flex max-h-[calc(100vh-260px)] min-h-[440px] flex-1 flex-col gap-4 overflow-y-auto p-5 sm:p-7">
              {messages.length === 0 && (
                <div className="m-auto flex max-w-[420px] flex-col items-center gap-3 text-center">
                  <h2 className="m-0 text-xl font-medium">Pick a model and start chatting</h2>
                  <p className="m-0 text-[13px] leading-relaxed text-ps-muted">
                    Each reply shows tokens in and out and what it cost.
                  </p>
                </div>
              )}
              <MessageList session={session} retryTokens={retryTokens} onRetry={retryTruncated} />
            </div>
            <div className="flex items-end gap-2.5 border-t border-ps-border p-3">
              <label htmlFor="playground-message" className="sr-only">
                Message
              </label>
              <input
                id="playground-message"
                className="input flex-1"
                placeholder={`Message ${selectedModel || 'the model'}…`}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && send()}
                disabled={busy}
              />
              {busy ? (
                <button className="btn-secondary" onClick={session.stop}>
                  <StopIcon className="h-4 w-4" /> Stop
                </button>
              ) : (
                <button className="btn-primary" onClick={send} disabled={!input.trim() || !selectedModel || keyMissing}>
                  <PaperAirplaneIcon className="h-4 w-4" /> Send
                </button>
              )}
            </div>
          </section>
        )}

        <aside aria-label="Settings" className="ps-card flex min-w-0 flex-[1_1_280px] flex-col gap-3.5 self-start p-[18px]">
          {mode === 'compare' && (
            <p className="m-0 text-xs leading-relaxed text-ps-subtle">
              These settings apply to both models, so the comparison is like for like.
            </p>
          )}
          <div className={mode === 'compare' ? 'hidden' : undefined}>
            <label htmlFor="playground-model" className={fieldLabel}>
              Model
            </label>
            <select
              id="playground-model"
              className="input"
              value={selectedModel}
              onChange={(e) => setModel(e.target.value)}
            >
              {CATALOG_CATEGORIES.map((c) => {
                const group = modelsByCategory.get(c.key);
                if (!group || group.length === 0) return null;
                return (
                  <optgroup key={c.key} label={c.label}>
                    {group.map((m) => (
                      <option key={m.id} value={m.display_name}>
                        {m.display_name}
                      </option>
                    ))}
                  </optgroup>
                );
              })}
              {(() => {
                const other = modelsByCategory.get('other');
                if (!other || other.length === 0) return null;
                return (
                  <optgroup label="Other">
                    {other.map((m) => (
                      <option key={m.id} value={m.display_name}>
                        {m.display_name}
                      </option>
                    ))}
                  </optgroup>
                );
              })()}
            </select>
            {entry && (entry.context_length > 0 || entryPrice) && (
              <div className="mt-2 flex flex-wrap justify-between gap-2 text-xs text-ps-subtle">
                {entry.context_length > 0 && <span>{entry.context_length.toLocaleString()} context</span>}
                {entryPrice && (
                  <span>{entryPrice.output ? `${entryPrice.input} · ${entryPrice.output}` : entryPrice.input}</span>
                )}
              </div>
            )}
          </div>
          {(mode === 'compare' || !isMedia) && (
            <div>
              <label htmlFor="playground-temperature" className={fieldLabel}>
                Temperature · {temperature.toFixed(1)}
              </label>
              <input
                id="playground-temperature"
                type="range"
                min={0}
                max={2}
                step={0.1}
                value={temperature}
                onChange={(e) => setTemperature(parseFloat(e.target.value))}
                className="w-full accent-primary-500"
              />
            </div>
          )}
          {(mode === 'compare' || !isMedia) && (
            <div>
              <label htmlFor="playground-max-tokens" className={fieldLabel}>
                Max tokens
              </label>
              <input
                id="playground-max-tokens"
                type="number"
                min={1}
                className="input"
                value={maxTokens}
                onChange={(e) => setMaxTokens(parseInt(e.target.value) || 1024)}
              />
            </div>
          )}

          <div>
            <label htmlFor="playground-auth" className={fieldLabel}>
              Authenticate as
            </label>
            <select
              id="playground-auth"
              className="input"
              value={authMode}
              onChange={(e) => setAuthMode(e.target.value as AuthMode)}
            >
              <option value="session">Session (logged in)</option>
              <option value="key">Use an API key…</option>
            </select>
            {authMode === 'key' && (
              <>
                <input
                  className="input mt-2"
                  placeholder="psk_..."
                  aria-label="API key"
                  value={authKey ?? ''}
                  onChange={(e) => setAuthKey(e.target.value)}
                />
                {keyMissing && (
                  <p className="mt-1 text-xs text-ps-subtle">Paste an API key to enable sending.</p>
                )}
              </>
            )}
          </div>

          {mode === 'chat' && !isMedia && (
            <div className="border-t border-ps-border pt-3">
              <button
                className="flex min-h-[28px] w-full items-center justify-between text-[13px] text-ps-text hover:text-white"
                onClick={() => setInspectorOpen((v) => !v)}
                aria-expanded={inspectorOpen}
              >
                Inspector
                {inspectorOpen ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
              </button>

              {inspectorOpen && (
                <div className="mt-3 space-y-3">
                  <div className="flex gap-1 rounded-md border border-ps-strong bg-ps-card p-1 text-xs">
                    {(['request', 'response', 'code'] as InspectorTab[]).map((t) => (
                      <button key={t} className={segBtn(inspectorTab === t)} onClick={() => setInspectorTab(t)}>
                        {t}
                      </button>
                    ))}
                  </div>

                  {inspectorTab === 'request' && (
                    <div className="relative">
                      <pre className={preCls}>
                        {lastRequest ? JSON.stringify(lastRequest, null, 2) : 'No request sent yet.'}
                      </pre>
                      {lastRequest && (
                        <button className={copyBtnCls} onClick={() => copy(JSON.stringify(lastRequest, null, 2))}>
                          <ClipboardIcon className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  )}

                  {inspectorTab === 'response' && (
                    <div className="relative">
                      <pre className={preCls}>{lastRawResponse || 'No response yet.'}</pre>
                      {lastRawResponse && (
                        <button className={copyBtnCls} onClick={() => copy(lastRawResponse)}>
                          <ClipboardIcon className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  )}

                  {inspectorTab === 'code' && (
                    <div className="space-y-2">
                      <div className="flex gap-1 rounded-md border border-ps-strong bg-ps-card p-1 text-xs">
                        {(['curl', 'python', 'javascript'] as CodeLang[]).map((l) => (
                          <button key={l} className={segBtn(codeLang === l)} onClick={() => setCodeLang(l)}>
                            {l}
                          </button>
                        ))}
                      </div>
                      <div className="relative">
                        <pre className={preCls}>{snippets[codeLang]}</pre>
                        <button className={copyBtnCls} onClick={() => copy(snippets[codeLang])}>
                          <ClipboardIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <Link to="/console/keys" className="ps-btn2 mt-auto">
            {mode === 'compare' ? 'Get an API key' : 'Get an API key for this model'}
          </Link>
        </aside>
      </div>
    </Page>
  );
};

export default Playground;
